import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { Building2, Trash2, ArrowLeft, Mail, Hash, Globe, CreditCard } from 'lucide-react';
import { getRoleTone } from '@shared/config/theme';
import { StatusChip } from '@shared/components/StatusChip';
import { useBreadcrumbLabel } from '@shared/contexts/BreadcrumbLabelContext';

interface Organization {
  id: string;
  name: string;
  slug: string;
  billing_email: string;
  data_region: string;
  tax_id: string;
  created_at: string;
  updated_at: string;
  role: string;
  joined_at: string;
}

export default function OrganizationDetail() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const locale = i18n.resolvedLanguage || i18n.language;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showConfirm } = useDialog();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [taxId, setTaxId] = useState('');
  const [dataRegion, setDataRegion] = useState('GLOBAL');

  useBreadcrumbLabel(organization && organization.id === id ? organization.name : null);

  const formatRole = (role: string) => {
    const roleKeys: Record<string, string> = {
      OWNER: 'owner',
      ADMIN: 'admin',
      MEMBER: 'member',
      VIEWER: 'viewer',
    };
    const key = roleKeys[role.toUpperCase()];
    return key ? t(`settings:organizations.roles.${key}`) : role;
  };

  const formatRegion = (region: string) => {
    const regionKeys: Record<string, string> = { GLOBAL: 'global', US: 'us', EU: 'eu' };
    const key = regionKeys[region.toUpperCase()];
    return key ? t(`settings:organizations.new.regions.${key}`) : region;
  };

  const formatDateTime = (value: string, dateOnly = false) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(
      locale,
      dateOnly
        ? { dateStyle: 'medium' }
        : {
            dateStyle: 'short',
            timeStyle: 'medium',
          }
    ).format(date);
  };

  useEffect(() => {
    if (id) {
      fetchOrganization(id);
    }
  }, [id]);

  const fetchOrganization = async (orgId: string) => {
    try {
      setLoading(true);
      const response = await api.get<{ organization: Organization }>(`/organizations/${orgId}`);
      setOrganization(response.data.organization);

      // Initialize form state
      setName(response.data.organization.name);
      setBillingEmail(response.data.organization.billing_email);
      setTaxId(response.data.organization.tax_id || '');
      setDataRegion(response.data.organization.data_region);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError(t('settings:organizations.errors.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!organization || !id) return;

    try {
      setSaving(true);
      await api.put(`/organizations/${id}`, {
        name,
        billing_email: billingEmail,
        tax_id: taxId,
        data_region: dataRegion,
      });

      await fetchOrganization(id);
      setEditing(false);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || t('settings:organizations.errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (organization) {
      setName(organization.name);
      setBillingEmail(organization.billing_email);
      setTaxId(organization.tax_id || '');
      setDataRegion(organization.data_region);
    }
    setEditing(false);
    setError('');
  };

  const handleDeleteOrg = async () => {
    if (!organization || !id) return;

    const confirmed = await showConfirm(
      t('settings:organizations.danger.confirmTitle'),
      t('settings:organizations.danger.confirmMessage', { name: organization.name }),
      'danger'
    );

    if (!confirmed) return;

    try {
      await api.delete(`/organizations/${id}`);
      navigate('/organizations');
    } catch (err: any) {
      setError(err.response?.data?.error || t('settings:organizations.errors.deleteFailed'));
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-xs">
          {t('settings:organizations.loading')}
        </p>
      </div>
    );
  }

  if (!organization) {
    return (
      <Card className="py-24 text-center border-2 border-dashed max-w-2xl mx-auto mt-12">
        <div className="w-20 h-20 bg-surface-2-light dark:bg-surface-2-dark rounded-3xl flex items-center justify-center mx-auto mb-6 text-text-subtle-light dark:text-text-subtle-dark">
          <Building2 size={40} />
        </div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
          {t('settings:organizations.notFound')}
        </h3>
        <Button variant="outline" className="mt-8" onClick={() => navigate('/organizations')}>
          {t('settings:organizations.returnToList')}
        </Button>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('settings:organizations.returnToList')}
              onClick={() => navigate('/organizations')}
              className="h-11 w-11 p-0 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
            >
              <ArrowLeft size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
            </Button>
            <div>
              <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                {organization.name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <StatusChip
                  tone={getRoleTone(organization.role)}
                  label={formatRole(organization.role)}
                  className="uppercase tracking-wider"
                />
                <span className="text-text-subtle-light dark:text-text-subtle-dark text-xs font-medium">
                  {t('settings:organizations.joined')}{' '}
                  {formatDateTime(organization.joined_at, true)}
                </span>
              </div>
            </div>
          </div>

          {!editing && organization.role === 'OWNER' && (
            <Button onClick={() => setEditing(true)} size="md">
              {t('settings:organizations.editButton')}
            </Button>
          )}
        </div>

        {error && (
          <div className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl text-state-danger-light dark:text-state-danger-dark font-medium text-sm">
            {error}
          </div>
        )}

        <Card className="shadow-elevated overflow-hidden">
          <div className="p-8 border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
            <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">
              {t('settings:organizations.detail.profile.title')}
            </h2>
          </div>

          <div className="p-8">
            {!editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <DetailItem
                  icon={<Building2 size={18} />}
                  label={t('settings:organizations.detail.profile.name')}
                  value={organization.name}
                />
                <DetailItem
                  icon={<Hash size={18} />}
                  label={t('settings:organizations.detail.profile.slug')}
                  value={organization.slug}
                  mono
                />
                <DetailItem
                  icon={<Mail size={18} />}
                  label={t('settings:organizations.detail.profile.billingEmail')}
                  value={organization.billing_email}
                />
                <DetailItem
                  icon={<CreditCard size={18} />}
                  label={t('settings:organizations.detail.profile.taxId')}
                  value={organization.tax_id || t('common:labels.notProvided')}
                />
                <DetailItem
                  icon={<Globe size={18} />}
                  label={t('settings:organizations.detail.profile.dataRegion')}
                  value={formatRegion(organization.data_region)}
                  badge
                />
                <DetailItem
                  icon={<Building2 size={18} />}
                  label={t('settings:organizations.detail.profile.createdAt')}
                  value={formatDateTime(organization.created_at)}
                />
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label={t('settings:organizations.name')}
                    id="organization-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <Input
                    label={t('settings:organizations.detail.profile.billingEmail')}
                    id="organization-billing-email"
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    required
                  />
                  <Input
                    label={`${t('settings:organizations.detail.profile.taxId')} (${t('common:actions.optional')})`}
                    id="organization-tax-id"
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                  />

                  <div>
                    <label
                      htmlFor="organization-data-region"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('settings:organizations.detail.profile.dataRegion')}
                    </label>
                    <select
                      id="organization-data-region"
                      value={dataRegion}
                      onChange={(e) => setDataRegion(e.target.value)}
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                    >
                      <option value="GLOBAL">
                        {t('settings:organizations.new.regions.global')}
                      </option>
                      <option value="US">{t('settings:organizations.new.regions.us')}</option>
                      <option value="EU">{t('settings:organizations.new.regions.eu')}</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    type="button"
                    className="flex-1"
                  >
                    {t('common:actions.cancel')}
                  </Button>
                  <Button type="submit" isLoading={saving} className="flex-1">
                    {t('settings:organizations.saveChanges')}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Card>

        {/* Danger Zone */}
        {organization.role === 'OWNER' && !editing && (
          <Card className="border-state-danger-light/20 dark:border-state-danger-dark/20 overflow-hidden">
            <div className="p-6 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-b border-state-danger-light/20 dark:border-state-danger-dark/20">
              <h3 className="text-lg font-black text-state-danger-light dark:text-state-danger-dark">
                {t('settings:organizations.danger.title')}
              </h3>
              <p className="text-sm text-state-danger-light/80 dark:text-state-danger-dark/80 mt-1">
                {t('settings:organizations.danger.description')}
              </p>
            </div>
            <div className="p-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-bold text-slate-900 dark:text-white">
                  {t('settings:organizations.danger.delete.title')}
                </p>
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1">
                  {t('settings:organizations.danger.delete.description')}
                </p>
              </div>
              <Button variant="danger" onClick={handleDeleteOrg} className="w-full sm:w-auto">
                <Trash2 size={16} className="mr-2" /> {t('common:actions.delete')}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

function DetailItem({ icon, label, value, mono, badge }: any) {
  return (
    <div className="space-y-2">
      <div className="flex items-center text-text-subtle-light dark:text-text-subtle-dark text-xs font-bold uppercase tracking-widest">
        <span className="mr-2">{icon}</span> {label}
      </div>
      {badge ? (
        <Badge variant="neutral" className="text-sm">
          {value}
        </Badge>
      ) : (
        <p className={`font-medium text-slate-900 dark:text-white ${mono ? 'font-mono' : ''}`}>
          {value}
        </p>
      )}
    </div>
  );
}
